import { AnalyticsInstance } from 'analytics';
import { Api } from '../api/Api';
import { ApiContextValue, Atoms, callbackFallback } from '../api/ApiContext';
import { createAppProvider } from './AppProvider';
import {
  createTranslate,
  Translate,
} from '../localization/translate/translate';
import {
  createTranslateContent,
  TranslateContent,
} from '../localization/translate/translateContent';
import {
  createUseApi,
  createUseApiAtom,
  createUseSharedApi,
  GetUseApi,
  GetUseApiAtom,
  GetUseSharedApi,
} from '../api';
import { FetchData } from '../api/FetchData';
import { Localization } from '../localization/Localization';
import { ThemeOverride } from '..';
import { TranslateError } from './ErrorBoundary';
import React from 'react';

export type App<
  TApi extends Api<any, any>,
  TLocalization extends Localization,
  TConfig,
> = {
  useApp: () => App<TApi, TLocalization, TConfig>;
  config: TConfig;
  // Api
  fetch: FetchData;
  apiContext: React.Context<ApiContextValue<TApi>>;
  Provider: React.FC<React.PropsWithChildren>;
  useApiAtom: GetUseApiAtom<TApi>;
  useApi: GetUseApi<TApi>;
  useSharedApi: GetUseSharedApi<TApi>;
  atoms: Atoms;
  _Api: TApi;
  _ApiContextValue: ApiContextValue<TApi>;
  // Localization
  translateError: TranslateError<App<TApi, TLocalization, TConfig>>;
  translateContent: TranslateContent<TLocalization>;
  translate: Translate<TLocalization>;
  localization: TLocalization;

  // AppConfig:

  // Analytics
  analyticsInstance: AnalyticsInstance;
  // theme
  theme?: ThemeOverride;
  // Testing
  colaUrl: string;
};

// TODO: move this to the recoil store
export let cachedApp: App<any, any, any> | null = null;

export function createApp<
  TApi extends Api,
  TLocalization extends Localization,
  TConfig,
>(
  appConfig: Partial<
    Pick<App<TApi, TLocalization, TConfig>, 'analyticsInstance' | 'theme'>
  > &
    Pick<
      App<TApi, TLocalization, TConfig>,
      'fetch' | 'localization' | 'config' | 'translateError' | 'colaUrl'
    >,
): App<TApi, TLocalization, TConfig> {
  if (cachedApp) {
    return cachedApp;
  }

  type TApp = App<TApi, TLocalization, TConfig>;

  const app: App<TApi, TLocalization, TConfig> = {
    apiContext: React.createContext<TApp['_ApiContextValue']>({
      atoms: {},
      setAtom: callbackFallback,
      removeAtom: callbackFallback,
      getAtom: callbackFallback,
      createFetch: callbackFallback,
    }),
    ...appConfig,
  } as App<TApi, TLocalization, TConfig>;

  app.translateContent = createTranslateContent(app.localization);
  app.translate = createTranslate(app.localization);

  app.useApp = function useApp() {
    return cachedApp || app;
  };

  app.useApi = createUseApi(app.fetch);
  app.Provider = createAppProvider(app);
  app.useApiAtom = createUseApiAtom(app);
  app.useSharedApi = createUseSharedApi(app);

  cachedApp = app;
  return app;
}
